import { useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { useIdentity } from '@zenbusiness/application-commons-identity';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { WebView } from '@zenbusiness/application-commons-webview';
import Banner from '@zenbusiness/zui/Banner';
import Button from '@zenbusiness/zui/Button';

import { accountSettingsSecurity } from '#client/constants/routes';
import { useMfaMethods } from '#client/hooks/useMfaMethods';
import useMyAccountOperation from '#client/hooks/useMyAccountOperation';
import { METHOD_TYPES } from '#client/pages/AccountSettings/components/TwoStepVerification/constants';
import {
  clearMfaNudgeDecision,
  dismissMfaNudge,
  isMfaNudgeHidden,
  readMfaNudgeDecision,
  saveMfaNudgeDecision,
  snoozeMfaNudge,
} from '#client/utils/mfaNudgeStorage';
import { MY_ACCOUNT_OPERATIONS } from '#client/utils/myAccount';
import { generateMfaEnrollmentUrl } from '#client/utils/urls';

export const LOCALIZED_TEXT = {
  setUp: 'dashboard.mfaNudgeBanner.setUp',
  notNow: 'dashboard.mfaNudgeBanner.notNow',
  dontAskAgain: 'dashboard.mfaNudgeBanner.dontAskAgain',
};

// Shown one at a time, in this order.
const NUDGES = {
  passkey: {
    title: 'dashboard.mfaNudgeBanner.passkey.title',
    subtitle: 'dashboard.mfaNudgeBanner.passkey.subtitle',
    snoozeDays: 90,
  },
  authenticator: {
    title: 'dashboard.mfaNudgeBanner.authenticator.title',
    subtitle: 'dashboard.mfaNudgeBanner.authenticator.subtitle',
    snoozeDays: 30,
  },
};

const NUDGE_NAMES = Object.keys(NUDGES);

const getMethodTypes = (methodKey) => METHOD_TYPES.find(({ key }) => key === methodKey).types;
const PHONE_TYPES = getMethodTypes('sms');

function MfaNudgeBanner() {
  const { formatMessage: fm } = useIntl();
  const { pathname } = useLocation();
  const { userUuid } = useIdentity();
  // Each read costs customers-api two Auth0 Management API calls, so the decision is kept per session.
  const cachedDecision = useMemo(() => (userUuid ? readMfaNudgeDecision(userUuid) : undefined), [userUuid]);
  const isCached = cachedDecision !== undefined;
  const allHidden = NUDGE_NAMES.every(isMfaNudgeHidden);
  // Each nudge signs the customer in again through Auth0, and the app's WebView clears the page's
  // storage on each load, which loses the sign-in state. The app shows none.
  const inWebView = Boolean(WebView?.isWebView);
  const { methods, requireMfa, passkeysAvailable, loading, error } = useMfaMethods({
    skip: !userUuid || isCached || allHidden || inWebView,
  });
  const startMyAccountOperation = useMyAccountOperation();
  const [hiddenNudge, setHiddenNudge] = useState(null);

  const confirmedTypes = methods.filter((m) => m.confirmed && !m.isRecoveryMethod).map(({ type }) => type);
  const hasPhone = confirmedTypes.some((type) => PHONE_TYPES.includes(type));
  const hasAuthenticator = confirmedTypes.includes('otp');
  // Adding a passkey makes a customer without two-step verification set up a text message first,
  // so only customers who already have it are nudged.
  const hasTwoStepVerification = confirmedTypes.some((type) => type !== 'passkey');
  // Passkeys exist only on the password connection.
  const wantsPasskey =
    passkeysAvailable && hasTwoStepVerification && !confirmedTypes.includes('passkey') && !isMfaNudgeHidden('passkey');

  // The nudge name, null for none, or undefined while the methods haven't answered.
  let nudge;
  if (inWebView) {
    nudge = null;
  } else if (isCached) {
    nudge = cachedDecision;
  } else if (!userUuid || allHidden || loading || error) {
    nudge = undefined;
  } else if (wantsPasskey) {
    nudge = 'passkey';
  } else {
    // Banking accepts only a recent text message, so an account that requires MFA is never nudged to the app.
    nudge = hasPhone && !hasAuthenticator && !requireMfa ? 'authenticator' : null;
  }

  useEffect(() => {
    if (!isCached && nudge !== undefined && userUuid) {
      saveMfaNudgeDecision(userUuid, nudge);
    }
  }, [isCached, nudge, userUuid]);

  // Once one is set aside, the next waits for another visit.
  if (!NUDGES[nudge] || hiddenNudge || isMfaNudgeHidden(nudge)) {
    return null;
  }

  const { title, subtitle, snoozeDays } = NUDGES[nudge];

  // Cleared so the next page load can pick the nudge after this one.
  const handleNotNow = () => {
    snoozeMfaNudge(nudge, snoozeDays);
    clearMfaNudgeDecision();
    setHiddenNudge(nudge);
  };

  const handleDontAskAgain = () => {
    dismissMfaNudge(nudge);
    clearMfaNudgeDecision();
    setHiddenNudge(nudge);
  };

  const primaryAction =
    nudge === 'passkey'
      ? {
          onClick: () => {
            clearMfaNudgeDecision();
            startMyAccountOperation(MY_ACCOUNT_OPERATIONS.passkey, { returnTo: accountSettingsSecurity });
          },
        }
      : { to: generateMfaEnrollmentUrl(pathname, 'otp'), onClick: clearMfaNudgeDecision };

  return (
    <Banner
      title={fm({ id: title })}
      subtitle={fm({ id: subtitle })}
      clickstreamProps={{ appSection: 'home', elementName: `mfa_nudge_${nudge}_banner` }}
      slots={{
        endIcon: (
          <Button
            clickstreamProps={{ appSection: 'home', elementName: `mfa_nudge_${nudge}_dont_ask_again` }}
            onClick={handleDontAskAgain}
            size="sm"
            variant="text"
            color="primary"
          >
            {fm({ id: LOCALIZED_TEXT.dontAskAgain })}
          </Button>
        ),
      }}
      slotProps={{
        primaryAction: {
          label: fm({ id: LOCALIZED_TEXT.setUp }),
          ...primaryAction,
        },
        secondaryAction: { label: fm({ id: LOCALIZED_TEXT.notNow }), onClick: handleNotNow },
      }}
    />
  );
}

export default MfaNudgeBanner;
